/**
 * Sticky add-to-cart bar — mirrors the currently selected variation on the
 * real single-product add-to-cart form and adds it to the cart via the
 * shared quick-add AJAX helper. Never re-implements variation selection:
 * it only ever reads whatever the customer already picked in the real form.
 */

import { ajaxAddToCart } from '../../woocommerce/cart-ajax.js';

export function initStickyAddToCart() {
	const bar = document.querySelector( '[data-sticky-add-to-cart]' );
	if ( ! bar ) return;

	const button = bar.querySelector( '[data-sticky-add-btn]' );
	const anchorForm = document.querySelector( '.pdp-variations-form, form.cart' );
	if ( ! button ) return;

	const config   = window.dbProductGrid || {};
	const messages = config.i18n || {};

	initVisibility( bar, anchorForm );

	button.addEventListener( 'click', async () => {
		if ( button.disabled ) return;

		const form = readRealForm( anchorForm, bar );

		if ( form.needsVariation ) {
			anchorForm?.scrollIntoView( { behavior: 'smooth', block: 'center' } );
			anchorForm?.classList.add( 'needs-selection' );
			window.setTimeout( () => anchorForm?.classList.remove( 'needs-selection' ), 900 );
			return;
		}

		const label       = button.querySelector( '.btn__label' );
		const originalText = label?.textContent || '';

		button.disabled = true;
		button.classList.add( 'is-loading' );
		if ( label ) label.textContent = messages.loading || originalText;

		const result = await ajaxAddToCart( {
			productId:   form.productId,
			variationId: form.variationId,
			quantity:    form.quantity,
			attributes:  form.attributes,
		} );

		button.classList.remove( 'is-loading' );
		button.classList.add( result.success ? 'is-success' : 'is-error' );
		if ( label ) {
			label.textContent = result.success
				? ( messages.added || originalText )
				: ( result.message || messages.error || originalText );
		}

		window.setTimeout( () => {
			button.classList.remove( 'is-success', 'is-error' );
			button.disabled = false;
			if ( label ) label.textContent = originalText;
		}, 2500 );
	} );
}

function initVisibility( bar, anchorForm ) {
	if ( ! anchorForm || ! ( 'IntersectionObserver' in window ) ) {
		bar.classList.add( 'is-visible' );
		return;
	}

	const observer = new IntersectionObserver(
		( entries ) => {
			entries.forEach( ( entry ) => {
				const scrolledPast = ! entry.isIntersecting && entry.boundingClientRect.top < 0;
				bar.classList.toggle( 'is-visible', scrolledPast );
			} );
		},
		{ threshold: 0 }
	);

	observer.observe( anchorForm );
}

function readRealForm( form, bar ) {
	const fallbackProductId = Number( bar.dataset.productId || 0 );

	if ( ! form ) {
		return { productId: fallbackProductId, variationId: 0, quantity: 1, attributes: {}, needsVariation: false };
	}

	const productId = Number(
		form.dataset.product_id || form.querySelector( 'input[name="product_id"]' )?.value || fallbackProductId
	);
	const isVariable     = form.classList.contains( 'variations_form' );
	const variationInput = form.querySelector( 'input[name="variation_id"]' );
	const variationId    = Number( variationInput?.value || 0 );
	const quantityInput  = form.querySelector( 'input[name="quantity"]' );
	const quantity       = Math.max( 1, Number( quantityInput?.value || 1 ) );

	const attributes = {};
	form.querySelectorAll( 'select[name^="attribute_"]' ).forEach( ( select ) => {
		attributes[ select.name ] = select.value;
	} );

	return {
		productId,
		variationId,
		quantity,
		attributes,
		needsVariation: isVariable && ! variationId,
	};
}

